import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from './useAuthContext';
import { toast } from 'sonner';
import { userApiClient, ApiError } from '../lib/api';
import { queryKeys } from '../lib/query/client';

/**
 * User preferences structure matching backend UserPreferences model.
 */
export interface UserPreferences {
  notifications: {
    emailEnabled: boolean;
    inAppEnabled: boolean;
    workflowCompletion: boolean;
    workflowFailure: boolean;
    teamInvitations: boolean;
    orgUpdates: boolean;
    membershipRequests: boolean;
  };
  ui: {
    theme: 'light' | 'dark' | 'system';
    timezone: string;
    sidebarState: 'expanded' | 'collapsed' | 'minimal';
    tableDensity: 'compact' | 'comfortable' | 'spacious';
    defaultOrganizationView: 'grid' | 'list';
  };
}

/**
 * Hook to fetch current user's preferences.
 * Preferences include UI settings and notification preferences.
 */
export function useUserPreferences() {
  // Gate the fetch on auth — public pages (marketing, /login) would
  // otherwise fire an unauthenticated GET that 401s on every render.
  const { isAuthenticated } = useAuthContext();
  return useQuery<UserPreferences, Error>({
    queryKey: queryKeys.userPreferences(),
    queryFn: async () => {
      try {
        return await userApiClient.get<UserPreferences>('/api/v1/users/me/preferences');
      } catch (error) {
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError('Failed to fetch preferences', 500, 'UnknownError');
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes - preferences don't change often
    retry: (failureCount, error) => {
      // Don't retry on authentication errors (401) - user needs to log in
      if (error instanceof ApiError && error.status === 401) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    // Don't throw errors to avoid crashing error boundaries (especially ThemeProvider)
    // Components using this hook should check for error state instead
    throwOnError: false,
  });
}

/**
 * Hook to update current user's preferences.
 * Supports partial updates - only send the preferences you want to change.
 *
 * @example
 * const { mutate } = useUpdateUserPreferences();
 *
 * // Update only theme
 * mutate({ ui: { theme: 'dark' } });
 *
 * // Update notifications
 * mutate({ notifications: { emailEnabled: false } });
 */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<UserPreferences>) => {
      const updatePromise = async () => {
        try {
          const updated = await userApiClient.request<UserPreferences>(
            '/api/v1/users/me/preferences',
            {
              method: 'PATCH',
              body: JSON.stringify(data),
            },
          );
          return updated;
        } catch (error) {
          if (error instanceof ApiError) {
            throw error;
          }
          throw new ApiError('Failed to update preferences', 500, 'UnknownError');
        }
      };

      const result = await toast
        .promise(updatePromise(), {
          loading: 'Updating preferences...',
          success: 'Preferences updated successfully',
          error: (err) => (err instanceof Error ? err.message : 'Failed to update preferences'),
        })
        .unwrap();
      await queryClient.refetchQueries({ queryKey: queryKeys.userPreferences() });
      return result;
    },
  });
}
